import { ChallengeIcon, DownloadIcon } from "@xola/icons";
import { Button, flash, Spinner } from "@xola/ui-kit";
import React, { useEffect, useState } from "react";
import { PaymentLineItem, Permission, Purchase } from "types";
import { DisputeChallengeDrawer } from "./DisputeChallengeDrawer";
import { DisputeSelectModal } from "./DisputeSelectModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useDisputeEvidence } from "@/hooks/useDisputeEvidence";
import { useModal } from "@/hooks/useVisibility";
import { getDisputedPaymentLineItems, hasPendingDispute } from "@/models/Purchase";

interface DisputeActionsProps {
    purchase: Purchase;
}

export const DisputeActions = ({ purchase }: DisputeActionsProps) => {
    const { isGranted } = useAuthorization();

    if (!isGranted(Permission.DISPUTE_CHALLENGE_VIEW)) {
        return null;
    }

    return <DisputeActionsContent purchase={purchase} />;
};

const DisputeActionsContent = ({ purchase }: DisputeActionsProps) => {
    const selectModal = useModal();
    const drawer = useModal();
    const [selectedPaymentId, setSelectedPaymentId] = useState<string>();
    const { isDownloading, downloadEvidence, error } = useDisputeEvidence(purchase.id);

    const disputedLineItems = getDisputedPaymentLineItems(purchase);
    const showChallenge = hasPendingDispute(purchase);

    useEffect(() => {
        if (error) {
            flash.danger(error);
        }
    }, [error]);

    const openDrawer = (paymentId: string) => {
        setSelectedPaymentId(paymentId);
        drawer.show();
    };

    const closeDrawer = () => {
        drawer.hide();
        setSelectedPaymentId(undefined);
    };

    const handleChallengeClick = () => {
        if (disputedLineItems.length === 1) {
            const paymentId = disputedLineItems[0].payment?.dispute?.stripeDestinationPayment;
            if (paymentId) {
                openDrawer(paymentId);
            } else {
                flash.danger("Unable to challenge this dispute. Payment information is missing.");
            }
        } else if (disputedLineItems.length > 1) {
            selectModal.show();
        }
    };

    const handleDisputeSelect = (lineItem: PaymentLineItem) => {
        selectModal.hide();
        const paymentId = lineItem.payment?.dispute?.stripeDestinationPayment;
        if (paymentId) {
            openDrawer(paymentId);
        } else {
            flash.danger("Unable to challenge this dispute. Payment information is missing.");
        }
    };

    return (
        <div>
            <div className="flex items-center space-x-2">
                <SecondaryButton
                    size="small"
                    icon={isDownloading ? <Spinner size="tiny" /> : <DownloadIcon size="small" />}
                    disabled={isDownloading}
                    data-testid="dispute-evidence-button"
                    onClick={downloadEvidence}
                >
                    Evidence
                </SecondaryButton>
                <Button
                    size="small"
                    color="primary"
                    icon={<ChallengeIcon size="small" />}
                    data-testid="dispute-challenge-button"
                    onClick={handleChallengeClick}
                >
                    {showChallenge ? "Challenge" : "View Dispute"}
                </Button>
            </div>

            <DisputeSelectModal
                isOpen={selectModal.isVisible}
                disputedLineItems={disputedLineItems}
                onSelect={handleDisputeSelect}
                onClose={selectModal.hide}
            />

            <DisputeChallengeDrawer isOpen={drawer.isVisible} paymentId={selectedPaymentId} onClose={closeDrawer} />
        </div>
    );
};
